/**
 * Combined header card (Combined Report.png): overlapping member avatars,
 * "«Surname» Family" serif title with the household badge chips, and the
 * score box + outline chips (ScorePanelView — same anatomy as the
 * individual full-screening header). While the family report is not
 * completed the score box gives way to a progress note (data-map §4
 * states; the design's mock shows the page alive pre-completion).
 */
import {
	COMBINED_REPORT_TITLE,
	type FamilyPredictions,
	combinedWaitingTally,
	deriveCombinedHeaderState,
} from '#app/utils/application-detail-v2/combined-display'
import type { GuarantorRequestsView } from '#app/utils/application-detail-v2/guarantor-requests'
import { isGuarantorRecommended } from '#app/utils/application-detail-v2/report-display'
import type {
	FamilyScoreSummary,
	GroupMember,
	ParsedStepData,
} from '#app/utils/application-detail-v2/types'
import { cn } from '#app/utils/misc'
import { HeaderBadges } from '../BadgePopovers'
import { ScorePanelView } from '../HeaderScorePanel'
import { DETAIL_CONTAINER } from '../primitives'
import { CombinedUpsellBody } from './CombinedUpsellCard'

const AVATAR_RING_TONES = [
	'border-green-500',
	'border-rose-400',
	'border-purple-400',
	'border-amber-400',
]

function initials(name: string): string {
	return (
		name
			.split(/\s+/)
			.filter(Boolean)
			.slice(0, 2)
			.map((part) => part.charAt(0).toUpperCase())
			.join('') || '?'
	)
}

function AvatarStack({ members }: { members: GroupMember[] }) {
	return (
		<div className="flex -space-x-3">
			{members.slice(0, 5).map((member, index) => (
				<span
					key={member.applicationId}
					title={member.displayName}
					className={cn(
						'flex size-14 items-center justify-center rounded-full border-2 bg-[#F4F4F2] text-base font-semibold text-[#32302F] ring-2 ring-white',
						AVATAR_RING_TONES[index % AVATAR_RING_TONES.length],
					)}
				>
					{initials(member.displayName)}
				</span>
			))}
		</div>
	)
}

export function CombinedHeader({
	members,
	householdPropertyStep,
	familyScore,
	familyPredictions,
	guarantorRequests,
	onRequestGuarantor,
	pendingInvitees = [],
	preScreenApplicants = [],
	onGetReport,
	scoreDisplay = 'band',
	isStudentHousing = false,
	regenerateOffered = false,
}: {
	members: GroupMember[]
	/** Primary property step overlaid with the household-max children/pets/
	 * vehicles (loader's deriveHouseholdComposition) — feeds the header chips. */
	householdPropertyStep: ParsedStepData['property']
	familyScore: FamilyScoreSummary | null
	familyPredictions: FamilyPredictions | null
	guarantorRequests: GuarantorRequestsView
	/** Opens the Request Guarantor dialog from the chip's popover — mirrors the
	 * individual header so a (first or additional) guarantor can be requested
	 * straight from the combined header. The chip uses the SAME inline popover as
	 * the individual page (status / recommend + request), not a separate dialog. */
	onRequestGuarantor?: () => void
	/** Family (re)generate offering, or null to hide the card CTA
	 * (deriveFamilyReportAction). In the non-completed branch this is only ever
	 * 'generate' (a completed report renders the score box instead). */
	/** Live invitations — people expected but not yet started; they count
	 * toward the "waiting on N submissions" line. */
	pendingInvitees?: Array<{ invitationType: 'co_applicant' | 'guarantor' }>
	/** Applicants still on pre-screening; once nobody is waiting, these are
	 * the only thing between the household and its combined report. */
	preScreenApplicants?: string[]
	/** Opens the upgrade confirm; absent without applications.fullscreen. */
	onGetReport?: () => void
	/** Opens the family (re)generate dialog from the failed/empty card. */
	/** Per-landlord score_display setting (DTO): band bar (default) or NN/100. */
	scoreDisplay?: 'band' | 'numeric'
	/** Student housing allows 1 applicant + ≥1 guarantor for family reports. */
	isStudentHousing?: boolean
	/** A (re)generate action is in the kebab — the failed copy points there. */
	regenerateOffered?: boolean
}) {
	const score = familyScore?.score ?? familyPredictions?.familyScore ?? 0
	const ready = familyScore?.processingStatus === 'completed'

	return (
		<div className={cn(DETAIL_CONTAINER, 'mt-6')}>
			<div className="flex flex-wrap items-center gap-6 rounded-3xl bg-[#FBFDFD] p-6 shadow-[0px_0px_16px_0px_#0000000A,0px_0px_4px_0px_#0000000A] sm:p-7 xl:flex-nowrap">
				<div className="flex min-w-0 flex-wrap items-center gap-6 xl:flex-1">
					{/* <AvatarStack members={members} /> */}
					<div className="min-w-0">
						{/* Never clip the household name: a hard slice dropped the trailing
						    "Family" ("Vandenberghe Family" → "Vandenberghe ") and cut
						    mid-word. Long names wrap instead — same rule as the individual
						    header and the FieldBox values. */}
						{/* Static household title (client 2026-07-28): the combined
						    surfaces no longer surface ANY person-derived name — the
						    "<Surname> Family" derivation was removed rather than left
						    unrendered, so the page, the PDF and the tab title all read
						    the same fixed label. */}
						<h1 className="font-calgary text-[25px] leading-tight font-medium wrap-break-word text-[#1F1E1D] md:text-[30px] lg:text-[40px]">
							{COMBINED_REPORT_TITLE}
						</h1>
						<HeaderBadges propertyStep={householdPropertyStep} />
					</div>
				</div>
				{ready ? (
					<div className="flex w-full min-w-0 justify-end xl:w-auto xl:shrink-0">
						<ScorePanelView
							score={score}
							guarantorRecommended={isGuarantorRecommended(
								familyPredictions?.guarantorScore ?? null,
							)}
							guarantorScore={familyPredictions?.guarantorScore ?? null}
							// The household's OWN guarantors decide this chip: green only
							// when every one of them is acceptable, red when none are,
							// amber when they disagree or are unjudged. With no guarantor
							// on the household it falls back to the recommendation.
							householdGuarantorVerdicts={members
								.filter((member) => member.applicationType === 'guarantor')
								.map((member) => member.guarantorAcceptable ?? null)}
							guarantorRequests={guarantorRequests}
							onRequestGuarantor={onRequestGuarantor}
							scoreDisplay={scoreDisplay}
						/>
					</div>
				) : (
					<div className="flex min-w-0 flex-1 basis-90 justify-end lg:flex-none lg:shrink-0">
						<div className="flex max-w-140 flex-col items-center gap-2 rounded-2xl border border-[#F0F0F0] bg-[#FBFDFD] px-8 py-6 text-center text-sm text-[#747474]">
							{(() => {
								// One pure decision tree (deriveCombinedHeaderState, tested):
								// generating → waiting → upgrade offer → unavailable with a
								// status line. Every non-waiting, non-generating state is
								// the SAME "Combined Report Unavailable" card (client
								// 2026-09-07) — a failed run used to say "generates
								// automatically once every member is in" with everyone in.
								const { waiting, expected } = combinedWaitingTally({
									members,
									pendingInvitees,
									isStudentHousing,
								})
								const state = deriveCombinedHeaderState({
									familyStatus: familyScore?.processingStatus ?? null,
									waiting,
									expected,
									preScreenApplicantCount: preScreenApplicants.length,
									regenerateOffered,
								})
								if (state.kind === 'generating') {
									return (
										<span className="text-amber-600">
											Combined report is generating…
										</span>
									)
								}
								if (state.kind === 'upsell') {
									return <CombinedUpsellBody onGetReport={onGetReport} />
								}
								return (
									<>
										<span className="flex items-center gap-2.5 text-[15px] font-semibold text-[#32302F] uppercase">
											<img
												src="/img/fluent_clock-warning-28-regular.svg"
												alt=""
												className="size-6 shrink-0"
											/>
											Combined Report Unavailable
										</span>
										<span>{state.body}</span>
									</>
								)
							})()}
						</div>
					</div>
				)}
			</div>
		</div>
	)
}
